import type {CSSProperties} from 'react';
// biome-ignore lint/correctness/noUnusedImports: we need React imported here for <Tag>
import React, {ElementType, HTMLAttributes, ReactNode} from 'react';
import type {Color} from '../types/global';
import {getColors} from '../utils/styles';

export type TypographyWeight = 'bold' | 'medium' | 'book';

export type TypographyVariant =
  | 'header0'
  | 'header1'
  | 'header2'
  | 'header3'
  | 'header4'
  | 'header5'
  | 'header6'
  | 'header7'
  | 'body'
  | 'paragraph1'
  | 'paragraph2'
  | 'paragraph3'
  | 'paragraph4'
  | 'list1'
  | 'list2'
  | 'label'
  | 'label1'
  | 'label2'
  | 'label3'
  | 'link'
  | 'premium';

export type TypographySize =
  | 'extra-extra-small'
  | 'extra-small'
  | 'smaller'
  | 'small'
  | 'medium'
  | 'large'
  | 'extra-large';

interface ComponentProps extends HTMLAttributes<HTMLOrSVGElement> {
  as?: ElementType;
}

const Component = ({as: Tag = 'div', ...otherProps}: ComponentProps) => {
  return <Tag {...otherProps} />;
};

const colors = getColors();

interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  variant: TypographyVariant;
  children: ReactNode;
  color?: Color;
  id?: string;
  weight?: TypographyWeight;
  size?: TypographySize;
  'data-qa-id'?: string;
  'data-qa-value'?: string | null | undefined;
  as?: string;
  inline?: boolean;
  semibold?: boolean;
  className?: string;
  style?: CSSProperties;
  htmlFor?: string;
  classNames?: never; // temporary hack to prevent devs from using classNames
}

export const Typography = ({
  id,
  as: htmlAs = 'p',
  className,
  color,
  size,
  inline,
  semibold,
  variant,
  style,
  children,
  'data-qa-id': dataQaId,
  'data-qa-value': dataQaValue,
  weight,
  ...props
}: TypographyProps) => {
  // If there is already a h1 element on the page, let's not duplicate it.

  // <Typography variant="h1" as="h2">
  //   This text uses an <h2> element but looks like an <h1>
  // </Typography>

  const inlineStyles = {...style};

  if (color) {
    inlineStyles.color = colors[color] || color;
  }

  const classNames = [
    className,
    variant,
    size,
    weight,
    inline && 'inline',
    semibold && 'semibold-text',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component
      as={htmlAs as ElementType}
      className={classNames}
      style={inlineStyles}
      data-qa-id={dataQaId}
      data-qa-value={dataQaValue}
      id={id}
      {...props}
    >
      {children}
    </Component>
  );
};
